import { FormEvent, useCallback, useEffect, useState } from 'react';
import ProfilePreview from './ProfilePreview';
import ProfileLinkEditor, { type ProfileLink } from './ProfileLinkEditor';
import ProfileFeedEditor, { type ProfileFeedEntry } from './ProfileFeedEditor';
import ProfileThemeSelector from './ProfileThemeSelector';
import type { ProfileThemeKey } from './themes';

const HANDLE_REGEX = /^[a-z0-9-]{3,32}$/;

export interface UserProfileData {
  user_id: string;
  handle: string | null;
  display_name: string | null;
  tagline: string | null;
  avatar_url: string | null;
  cover_url: string | null;
  bio_md: string | null;
  location: string | null;
  theme: string | null;
  quote_text: string | null;
  quote_attr: string | null;
  links: ProfileLink[] | null;
  feed_entries: ProfileFeedEntry[] | null;
  visibility: string | null;
}

interface ProfileEditorProps {
  profile: UserProfileData;
  role?: string | null;
  onSave: (updates: Record<string, unknown>) => Promise<void>;
  onCheckHandle?: (handle: string) => Promise<boolean>;
}

export default function ProfileEditor({ profile, role, onSave, onCheckHandle }: ProfileEditorProps) {
  const [handle, setHandle] = useState(profile.handle || '');
  const [displayName, setDisplayName] = useState(profile.display_name || '');
  const [tagline, setTagline] = useState(profile.tagline || '');
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || '');
  const [coverUrl, setCoverUrl] = useState(profile.cover_url || '');
  const [bioMd, setBioMd] = useState(profile.bio_md || '');
  const [location, setLocation] = useState(profile.location || '');
  const [theme, setTheme] = useState(profile.theme || 'sunrise');
  const [quoteText, setQuoteText] = useState(profile.quote_text || '');
  const [quoteAttr, setQuoteAttr] = useState(profile.quote_attr || '');
  const [links, setLinks] = useState<ProfileLink[]>((profile.links as ProfileLink[]) || []);
  const [feedEntries, setFeedEntries] = useState<ProfileFeedEntry[]>((profile.feed_entries as ProfileFeedEntry[]) || []);
  const [visibility, setVisibility] = useState(profile.visibility || 'public');

  const [handleStatus, setHandleStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Handle availability check with debounce
  useEffect(() => {
    if (!handle || handle === profile.handle || !onCheckHandle) {
      setHandleStatus('idle');
      return;
    }
    if (!HANDLE_REGEX.test(handle)) {
      setHandleStatus('idle');
      return;
    }

    setHandleStatus('checking');
    const timer = setTimeout(async () => {
      const available = await onCheckHandle(handle);
      setHandleStatus(available ? 'available' : 'taken');
    }, 400);
    return () => clearTimeout(timer);
  }, [handle, profile.handle, onCheckHandle]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await onSave({
        handle: handle || undefined,
        display_name: displayName || undefined,
        tagline: tagline || null,
        avatar_url: avatarUrl || null,
        cover_url: coverUrl || null,
        bio_md: bioMd || null,
        location: location || null,
        theme,
        quote_text: quoteText || null,
        quote_attr: quoteAttr || null,
        links,
        feed_entries: feedEntries,
        visibility,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  }, [handle, displayName, tagline, avatarUrl, coverUrl, bioMd, location, theme, quoteText, quoteAttr, links, feedEntries, visibility, onSave]);

  return (
    <div className="space-y-8">
      {/* Live preview */}
      <section>
        <h2 className="mb-4 text-lg font-semibold text-cocoa">Preview</h2>
        <ProfilePreview
          displayName={displayName}
          tagline={tagline}
          quoteText={quoteText}
          quoteAttribution={quoteAttr}
          themeKey={theme}
          avatarUrl={avatarUrl}
          coverUrl={coverUrl}
          links={links}
          feedEntries={feedEntries}
          role={role}
        />
      </section>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">Basic Info</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-cocoa">
              Handle
              <div className="relative">
                <input
                  className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="your-handle"
                  maxLength={32}
                />
                {handleStatus === 'checking' && <span className="absolute right-3 top-3 text-xs text-cocoa/50">checking...</span>}
                {handleStatus === 'available' && <span className="absolute right-3 top-3 text-xs text-emerald-600">available</span>}
                {handleStatus === 'taken' && <span className="absolute right-3 top-3 text-xs text-brick">taken</span>}
              </div>
            </label>
            <label className="text-sm font-medium text-cocoa">
              Display Name
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your Name"
                maxLength={100}
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Tagline
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="A short description"
                maxLength={200}
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Location
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, State"
                maxLength={100}
              />
            </label>
          </div>
          <label className="block text-sm font-medium text-cocoa">
            Bio
            <textarea
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
              rows={4}
              value={bioMd}
              onChange={(e) => setBioMd(e.target.value)}
              placeholder="Tell people about yourself. Markdown supported."
              maxLength={4000}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium text-cocoa">
              Avatar URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://..."
              />
            </label>
            <label className="text-sm font-medium text-cocoa">
              Cover Image URL
              <input
                className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
                value={coverUrl}
                onChange={(e) => setCoverUrl(e.target.value)}
                placeholder="https://..."
              />
            </label>
          </div>
        </section>

        {/* Quote */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">Quote</h3>
          <label className="block text-sm font-medium text-cocoa">
            Quote text
            <textarea
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
              rows={2}
              value={quoteText}
              onChange={(e) => setQuoteText(e.target.value)}
              placeholder="A quote that represents you"
              maxLength={500}
            />
          </label>
          <label className="block text-sm font-medium text-cocoa">
            Attribution
            <input
              className="mt-1 w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none"
              value={quoteAttr}
              onChange={(e) => setQuoteAttr(e.target.value)}
              placeholder="— Author"
              maxLength={100}
            />
          </label>
        </section>

        {/* Theme */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">Theme</h3>
          <ProfileThemeSelector value={theme} onChange={(t) => setTheme(t)} />
        </section>

        {/* Links */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">Links</h3>
          <ProfileLinkEditor links={links} onChange={setLinks} />
        </section>

        {/* Feed */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <ProfileFeedEditor entries={feedEntries} onChange={setFeedEntries} />
        </section>

        {/* Visibility */}
        <section className="space-y-4 rounded-2xl border border-sand/70 bg-white/70 p-6">
          <h3 className="text-lg font-semibold text-cocoa">Visibility</h3>
          <select
            className="w-full rounded-xl border border-sand px-3 py-2 text-sm text-cocoa focus:border-cocoa focus:outline-none sm:w-auto"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
          >
            <option value="public">Public — anyone can view</option>
            <option value="members">Members — logged-in users only</option>
            <option value="private">Private — hidden</option>
          </select>
        </section>

        {/* Submit */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={saving || handleStatus === 'taken'}
            className="rounded-xl bg-cocoa px-6 py-3 text-sm font-semibold text-white transition hover:bg-cocoa/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
          {saved && <span className="text-sm font-medium text-emerald-600">Saved!</span>}
        </div>
      </form>
    </div>
  );
}
